import {Injectable} from '@angular/core';
import {TranslocoService} from '@ngneat/transloco';
import Swal, {SweetAlertIcon, SweetAlertResult} from 'sweetalert2';
import {AlertType} from '../../models/enum/alert-type.enum';

@Injectable({
    providedIn: 'root'
})
export class AlertService {
    constructor(private translateService: TranslocoService) {}

    success(message?: string): void {
        let defaultMessage = 'generico.Sucesso';
        if (message) {
            defaultMessage = message;
        }
        this.alert(AlertType.SUCCESS, defaultMessage);
    }

    warning(message: string): void {
        let defaultMessage = 'generico.Erro';
        if (message) {
            defaultMessage = message;
        }
        this.alert(AlertType.WARNING, defaultMessage);
    }

    info(message: string): void {
        this.alert(AlertType.INFO, message);
    }

    async confirmDelete(message: string, title: string = 'Confirmar exclusão'): Promise<boolean> {
        const result = await this.confirm({
            title,
            text: message,
            confirmButtonText: 'Sim, excluir'
        });

        return result.isConfirmed;
    }

    async confirm(options: {
        title: string;
        text: string;
        confirmButtonText?: string;
        cancelButtonText?: string;
        icon?: SweetAlertIcon;
    }): Promise<SweetAlertResult> {
        return Swal.fire({
            title: options.title,
            text: options.text,
            icon: options.icon ?? 'warning',
            allowOutsideClick: false,
            showCancelButton: true,
            confirmButtonText: options.confirmButtonText ?? 'Confirmar',
            cancelButtonText: options.cancelButtonText ?? 'Cancelar',
            reverseButtons: true
        });
    }

    openSnackBar(message: string, alertType: AlertType, action?: string, duration?: number): void {
        const translatedMessage = this.translateMessage(message);
        const translatedAction = action ? this.translateMessage(action) : 'Ok';

        void Swal.fire({
            toast: true,
            position: 'top-end',
            icon: this.getIcon(alertType),
            title: translatedMessage,
            showConfirmButton: !!action,
            confirmButtonText: translatedAction,
            timer: action ? undefined : duration ?? 5000,
            timerProgressBar: !action,
            customClass: {
                popup: 'alert-' + alertType
            }
        });
    }

    private alert(alertType: AlertType, message: string): void {
        this.openSnackBar(message, alertType);
    }

    private translateMessage(message: string): string {
        const translatedMessage = this.translateService.translate(message);
        return translatedMessage || message;
    }

    private getIcon(alertType: AlertType): SweetAlertIcon {
        switch (alertType) {
            case AlertType.SUCCESS:
                return 'success';
            case AlertType.WARNING:
                return 'warning';
            case AlertType.INFO:
            default:
                return 'info';
        }
    }
}
